import { isTouchUi } from "./device_profile.js";

/** Georgian Lari (GEL) — Tbilisi market display. */
export const CURRENCY_CODE = "GEL";
export const CURRENCY_LABEL = "ლარი";

/** Aspect partner furniture houses shown on object hover cards. */
export const ASPECT_PARTNER_BRANDS = ["Norn", "Softfield", "Lumen & Oak", "Casa Linea"];

// ---------------------------------------------------------------------------
// Display currency (USD default, Lari optional). Catalog prices are stored in
// GEL; USD display converts at a fixed market-ish rate. Persisted per browser
// on desktop. Phones always show USD.
// ---------------------------------------------------------------------------
const CURRENCY_STORAGE_KEY = "aspect-currency";
export const GEL_PER_USD = 2.7;

function mobileShowsUsd() {
  return isTouchUi();
}

let _activeCurrency = (() => {
  if (mobileShowsUsd()) return "USD";
  try {
    const v = localStorage.getItem(CURRENCY_STORAGE_KEY);
    return v === "GEL" ? "GEL" : "USD";
  } catch {
    return "USD";
  }
})();

export function getCurrency() {
  if (mobileShowsUsd()) return "USD";
  return _activeCurrency;
}

/** Switch display currency ("GEL" | "USD") and notify listeners. */
export function setCurrency(code) {
  const next = code === "USD" ? "USD" : "GEL";
  if (next === _activeCurrency) return;
  _activeCurrency = next;
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, next);
  } catch {
    /* private mode */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("aspect:currency-changed", { detail: { currency: next } }));
  }
}

/**
 * Format a GEL amount in the active display currency, e.g. `450 ₾` or `$167`.
 * (Name kept for the existing call sites — amounts are always stored in GEL.)
 */
export function formatGelPrice(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  if (getCurrency() === "USD") {
    const usd = Math.round(Number(amount) / GEL_PER_USD);
    return `$${usd.toLocaleString("en-US")}`;
  }
  const n = Math.round(Number(amount));
  return `${n.toLocaleString("ka-GE")} ₾`;
}

/** Stable 32-bit hash so the same object always gets the same brand / sale %. */
export function stableHash(key) {
  const s = String(key ?? "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Pick a partner brand for a catalog / placement id. */
export function partnerBrandForId(id) {
  const i = stableHash(id) % ASPECT_PARTNER_BRANDS.length;
  return ASPECT_PARTNER_BRANDS[i];
}

/**
 * Deterministic Aspect sale for an object: 35–50% off list price, so the
 * whole-home savings summary always lands in the 35–50% band.
 * @param {number|null|undefined} listPrice
 * @param {string|number|null|undefined} id stable key (placement / asset id)
 * @returns {{ list: number, sale: number, pct: number } | null}
 */
export function aspectSalePrice(listPrice, id) {
  const list = Number(listPrice);
  if (!Number.isFinite(list) || list <= 0) return null;
  // 35..50 inclusive, stable per object
  const pct = 35 + (stableHash(`sale:${id ?? list}`) % 16);
  const sale = Math.max(1, Math.round(list * (1 - pct / 100)));
  return { list: Math.round(list), sale, pct };
}
